import { useState, useCallback, useRef } from 'react'
import { publicService } from '@/services'
import { toUTCDateString, parseDonorPortalSearchPayload } from '@/utils'
import { normalizeCityInput } from '@/utils/city'
import { createRequestSequencer } from '@/utils/request-sequencer'

export const useGetDonorCenters = () => {
  const [donorCenters, setDonorCenters] = useState([])
  const [loadingDonorCenters, setLoadingDonorCenters] = useState(false)

  const getDonorCenters = async () => {
    setLoadingDonorCenters(true)
    try {
      const response = await publicService.getDonorCenters()
      setDonorCenters(response?.data ?? [])
    } finally {
      setLoadingDonorCenters(false)
    }
  }

  return { donorCenters, loadingDonorCenters, getDonorCenters }
}

export const useGetDonorCenterCalendar = () => {
  const [donorCenterSessions, setDonorCenterSessions] = useState([])
  const [loadingDonorCenterCalendar, setLoadingDonorCenterCalendar] =
    useState(false)

  const getDonorCenterCalendar = useCallback(async params => {
    setLoadingDonorCenterCalendar(true)
    setDonorCenterSessions([])
    try {
      const response = await publicService.getDonorCenterCalendar(params)
      setDonorCenterSessions(response?.data ?? [])
    } finally {
      setLoadingDonorCenterCalendar(false)
    }
  }, [])

  return {
    donorCenterSessions,
    getDonorCenterCalendar,
    loadingDonorCenterCalendar,
  }
}

export const useGetZipCodeSearch = () => {
  const [zipCodeSearchResults, setZipCodeSearchResults] = useState([])
  const [zipCodeSearchSuggestions, setZipCodeSearchSuggestions] = useState([])
  const [loadingZipCodeSearch, setLoadingZipCodeSearch] = useState(false)

  const getZipCodeSearch = useCallback(async formData => {
    setLoadingZipCodeSearch(true)
    setZipCodeSearchResults([])
    setZipCodeSearchSuggestions([])
    try {
      const params = {
        zip_code: String(formData.zip_code || '').trim(),
        start_date: toUTCDateString(formData.start_date),
        procedure_type_id: formData.procedure_type_id || undefined,
        include_mobile_drives: formData.include_mobile_drives !== false,
        include_donor_centers: formData.include_donor_centers !== false,
      }
      if (formData.end_date) {
        params.end_date = toUTCDateString(formData.end_date)
      }
      const response = await publicService.getZipCodeSearch(params)
      const { results, suggestions } = parseDonorPortalSearchPayload(
        response?.data
      )
      setZipCodeSearchResults(results)
      setZipCodeSearchSuggestions(suggestions)
    } finally {
      setLoadingZipCodeSearch(false)
    }
  }, [])

  const clearZipCodeSearch = useCallback(() => {
    setZipCodeSearchResults([])
    setZipCodeSearchSuggestions([])
  }, [])

  return {
    zipCodeSearchResults,
    zipCodeSearchSuggestions,
    getZipCodeSearch,
    loadingZipCodeSearch,
    clearZipCodeSearch,
  }
}

export const useGetCitySearch = () => {
  const [citySearchResults, setCitySearchResults] = useState([])
  const [citySearchSuggestions, setCitySearchSuggestions] = useState([])
  const [loadingCitySearch, setLoadingCitySearch] = useState(false)

  const getCitySearch = useCallback(async formData => {
    setLoadingCitySearch(true)
    setCitySearchResults([])
    setCitySearchSuggestions([])
    try {
      const params = {
        city: normalizeCityInput(formData.city),
        start_date: toUTCDateString(formData.start_date),
        procedure_type_id: formData.procedure_type_id || undefined,
        include_mobile_drives: formData.include_mobile_drives !== false,
        include_donor_centers: formData.include_donor_centers !== false,
      }
      if (formData.end_date) {
        params.end_date = toUTCDateString(formData.end_date)
      }
      const response = await publicService.getCitySearch(params)
      const { results, suggestions } = parseDonorPortalSearchPayload(
        response?.data
      )
      setCitySearchResults(results)
      setCitySearchSuggestions(suggestions)
    } finally {
      setLoadingCitySearch(false)
    }
  }, [])

  const clearCitySearch = useCallback(() => {
    setCitySearchResults([])
    setCitySearchSuggestions([])
  }, [])

  return {
    citySearchResults,
    citySearchSuggestions,
    getCitySearch,
    loadingCitySearch,
    clearCitySearch,
  }
}

export const useGetSponsors = () => {
  const [sponsors, setSponsors] = useState([])
  const [loadingSponsors, setLoadingSponsors] = useState(false)

  const getSponsors = async () => {
    setLoadingSponsors(true)
    try {
      const response = await publicService.getSponsors()
      setSponsors(response?.data ?? [])
    } finally {
      setLoadingSponsors(false)
    }
  }

  return { sponsors, loadingSponsors, getSponsors }
}

export const useGetCities = () => {
  const [cities, setCities] = useState([])
  const [loadingCities, setLoadingCities] = useState(false)

  const getCities = useCallback(async () => {
    setLoadingCities(true)
    try {
      const response = await publicService.getCities()
      setCities(response?.data ?? [])
    } finally {
      setLoadingCities(false)
    }
  }, [])

  return { cities, loadingCities, getCities }
}

export const useGetSponsorSearch = () => {
  const [sponsorSearchResults, setSponsorSearchResults] = useState([])
  const [sponsorSearchSuggestions, setSponsorSearchSuggestions] = useState([])
  const [loadingSponsorSearch, setLoadingSponsorSearch] = useState(false)

  const getSponsorSearch = useCallback(async formData => {
    setLoadingSponsorSearch(true)
    setSponsorSearchResults([])
    setSponsorSearchSuggestions([])
    try {
      const params = {
        account_id: String(formData.account_id || '').trim(),
        start_date: toUTCDateString(formData.start_date),
        procedure_type_id: formData.procedure_type_id || undefined,
      }
      if (formData.end_date) {
        params.end_date = toUTCDateString(formData.end_date)
      }
      const response = await publicService.getSponsorSearch(params)
      const { results, suggestions } = parseDonorPortalSearchPayload(
        response?.data
      )
      setSponsorSearchResults(results)
      setSponsorSearchSuggestions(suggestions)
    } finally {
      setLoadingSponsorSearch(false)
    }
  }, [])

  const clearSponsorSearch = useCallback(() => {
    setSponsorSearchResults([])
    setSponsorSearchSuggestions([])
  }, [])

  return {
    sponsorSearchResults,
    sponsorSearchSuggestions,
    getSponsorSearch,
    loadingSponsorSearch,
    clearSponsorSearch,
  }
}

export const useGetPrivateDriveSearch = () => {
  const [privateDriveError, setPrivateDriveError] = useState(null)
  const [loadingPrivateDriveSearch, setLoadingPrivateDriveSearch] =
    useState(false)

  const getPrivateDriveSearch = useCallback(async formData => {
    setLoadingPrivateDriveSearch(true)
    setPrivateDriveError(null)
    try {
      const params = {
        drive_id: String(formData.drive_id || '').trim(),
        procedure_type_id: formData.procedure_type_id || undefined,
      }
      const response = await publicService.getPrivateDriveSearch(params)
      if (response?.status === 'success' && response?.data) {
        return { ok: true, data: response.data }
      }
      const message = response?.response || 'Drive not found.'
      setPrivateDriveError(message)
      return { ok: false, message }
    } catch (err) {
      const message =
        err?.response?.data?.response ||
        err?.response?.data?.message ||
        'Drive not found.'
      setPrivateDriveError(message)
      return { ok: false, message }
    } finally {
      setLoadingPrivateDriveSearch(false)
    }
  }, [])

  const clearPrivateDriveSearch = useCallback(() => {
    setPrivateDriveError(null)
  }, [])

  return {
    privateDriveError,
    getPrivateDriveSearch,
    loadingPrivateDriveSearch,
    clearPrivateDriveSearch,
  }
}

export const useGetProcedureTypes = () => {
  const [procedureTypes, setProcedureTypes] = useState([])
  const [loadingProcedureTypes, setLoadingProcedureTypes] = useState(false)

  const getProcedureTypes = async () => {
    setLoadingProcedureTypes(true)
    try {
      const response = await publicService.getProcedureTypes()
      setProcedureTypes(response?.data)
    } finally {
      setLoadingProcedureTypes(false)
    }
  }

  return { procedureTypes, loadingProcedureTypes, getProcedureTypes }
}

export const useGetOperationSchedules = () => {
  const [operationSchedules, setOperationSchedules] = useState([])
  const [operationScheduleSuggestions, setOperationScheduleSuggestions] =
    useState([])
  const [loadingOperationSchedules, setLoadingOperationSchedules] =
    useState(false)

  const getOperationSchedules = async formData => {
    try {
      const params = {
        date: toUTCDateString(formData.date),
      }
      if (formData.procedure_type_id) {
        params.procedure_type_id = formData.procedure_type_id
      }
      setOperationSchedules([])
      setOperationScheduleSuggestions([])
      setLoadingOperationSchedules(true)
      const response = await publicService.getOperationSchedules(params)
      const { results, suggestions } = parseDonorPortalSearchPayload(
        response?.data
      )
      setOperationSchedules(results)
      setOperationScheduleSuggestions(suggestions)
    } finally {
      setLoadingOperationSchedules(false)
    }
  }

  return {
    operationSchedules,
    operationScheduleSuggestions,
    getOperationSchedules,
    loadingOperationSchedules,
  }
}

export const useGetOperationLocations = () => {
  const [operationLocationsMap, setOperationLocationsMap] = useState({})
  const [loadingOperationLocations, setLoadingOperationLocations] =
    useState(true)

  const getOperationLocations = async formData => {
    try {
      const params = {
        date: toUTCDateString(formData.date),
      }
      if (formData.procedure_type_id) {
        params.procedure_type_id = formData.procedure_type_id
      }
      setLoadingOperationLocations(true)
      const response = await publicService.getOperationLocations(params)
      const newData = response?.data || []

      // Convert response to object format: { date: [operations] }
      const locationsObject = {}
      newData.forEach(item => {
        locationsObject[item.date] = item.operations || []
      })

      setOperationLocationsMap(locationsObject)
    } finally {
      setLoadingOperationLocations(false)
    }
  }

  const operationLocations = Object.values(operationLocationsMap).flat()

  return {
    operationLocations,
    operationLocationsMap,
    getOperationLocations,
    loadingOperationLocations,
  }
}

export const useGetAppointmentAvailability = () => {
  const [appointmentAvailability, setAppointmentAvailability] = useState([])
  const [loadingAppointmentAvailability, setLoadingAppointmentAvailability] =
    useState(false)
  const sequencerRef = useRef(createRequestSequencer())

  const getAppointmentAvailability = useCallback(async formData => {
    const seq = sequencerRef.current
    const { id, signal } = seq.next()
    setLoadingAppointmentAvailability(true)
    try {
      const params = {
        operation_id: formData.operation_id,
        operation_type: formData.operation_type,
      }
      if (formData.procedure_type_id) {
        params.procedure_type_id = formData.procedure_type_id
      }
      const response = await publicService.getAppointmentAvailability(
        params,
        signal
      )
      if (!seq.isLatest(id)) return
      setAppointmentAvailability(response?.data)
    } catch (error) {
      if (!seq.isLatest(id)) return
      throw error
    } finally {
      if (seq.isLatest(id)) setLoadingAppointmentAvailability(false)
    }
  }, [])

  const clearAppointmentAvailability = useCallback(() => {
    sequencerRef.current.invalidate()
    setAppointmentAvailability([])
    setLoadingAppointmentAvailability(false)
  }, [])

  return {
    appointmentAvailability,
    getAppointmentAvailability,
    clearAppointmentAvailability,
    loadingAppointmentAvailability,
  }
}

export const useGetDriveSessionSingle = () => {
  const [driveSessionSingle, setDriveSessionSingle] = useState(null)
  const [loadingDriveSessionSingle, setLoadingDriveSessionSingle] =
    useState(false)
  const sequencerRef = useRef(createRequestSequencer())

  const getDriveSessionSingle = useCallback(async params => {
    const seq = sequencerRef.current
    const { id, signal } = seq.next()
    setLoadingDriveSessionSingle(true)
    setDriveSessionSingle(null)
    try {
      const response = await publicService.getDriveSessionSingle(params, signal)
      if (!seq.isLatest(id)) return null
      setDriveSessionSingle(response?.data ?? null)
      return response?.data ?? null
    } catch (error) {
      if (!seq.isLatest(id)) return null
      throw error
    } finally {
      if (seq.isLatest(id)) setLoadingDriveSessionSingle(false)
    }
  }, [])

  return {
    driveSessionSingle,
    getDriveSessionSingle,
    loadingDriveSessionSingle,
  }
}
